import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useFormikContext } from 'formik';

import { InviteType } from 'constants/enums';
import { useAppSelector } from 'hooks';
import ShippingSpeedField from 'pages/Dashboard/Common/Devices/ShippingSpeedField';
import { getDeviceOrderings } from 'store/groups/reducer';
import { cartHasAmazonItem, resolveCartItems } from 'utils/Devices';

import DevicesList from './DevicesList';
import ShippingAddressForm from './ShippingAddressForm';
import { IInvitation, IShippingInfo } from '../Onboard';

interface IShippingInfoProps {
  devices: IDeviceOrderingCatalogItem[];
  inviteType: InviteType;
  showAddressToggle?: boolean;
  homeShippingCandidate?: IShippingInfo;
  lastShippingCandidate?: IShippingInfo;
}

const ShippingInfo = ({
  devices,
  inviteType,
  showAddressToggle = false,
  homeShippingCandidate,
  lastShippingCandidate
}: IShippingInfoProps) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<IInvitation>();
  const isBulkFlow =
    inviteType === InviteType.BULK_ORDER_DEVICES ||
    inviteType === InviteType.BULK_ORDER_ACCESSORIES;
  // The full catalogue, not the step's filtered `devices`: a cuff chosen with a monitor is an
  // option of that monitor and only the catalogue knows which.
  const catalog = useAppSelector(getDeviceOrderings);

  const cart = useMemo(
    () => ({ ...values.devices, ...values.accessories }),
    [values.devices, values.accessories]
  );
  const orderItems = useMemo(() => resolveCartItems(cart, catalog), [cart, catalog]);
  const hasAmazonItem = useMemo(() => cartHasAmazonItem(cart, catalog), [cart, catalog]);

  /**
   * Prefill on change rather than on mount. The wizard builds its steps up front, so this step
   * mounted before the demographics were typed and `useMount` captured empty strings: the contact
   * fields arrived blank on a patient whose phone and email we had (QA v26.10, #11).
   *
   * Only ever fills a field that is still empty, so it never overwrites what someone typed.
   */
  const prefill = (field: string, value?: string | null) => {
    if (value && !values.shippingInfo[field as keyof IShippingInfo]) {
      setFieldValue(`shippingInfo.${field}`, value);
    }
  };

  useEffect(() => {
    if (isBulkFlow) return;
    prefill('firstName', values.patientInfo.firstName);
    prefill('lastName', values.patientInfo.lastName);
    prefill('email', values.patientInfo.email_address);
    prefill('phoneNumber', values.patientInfo.phoneNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isBulkFlow,
    values.patientInfo.firstName,
    values.patientInfo.lastName,
    values.patientInfo.email_address,
    values.patientInfo.phoneNumber
  ]);

  return (
    <>
      <h4 className="mb-4" data-test="shipping-info-title">
        {intl.formatMessage({ id: 'shippingInformationKey' })}
      </h4>
      <DevicesList
        selectedDevices={cart}
        devices={devices}
        displayQuantity={isBulkFlow}
        dataTestId="shipping-info-devices"
      />
      <ShippingAddressForm
        gid={values.gid}
        inviteType={inviteType}
        showAddressToggle={showAddressToggle}
        homeShippingCandidate={homeShippingCandidate}
        lastShippingCandidate={lastShippingCandidate}
      />
      {/* Bulk orders ship to the practice at Standard; Expedited is shown but cannot be chosen (D1). */}
      <ShippingSpeedField
        gid={values.gid}
        items={orderItems}
        enabled={hasAmazonItem}
        bulk={isBulkFlow}
      />
    </>
  );
};

export default ShippingInfo;
